import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PiggyBank, Undo2, Scissors, Search } from "lucide-react";
import api from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, StatCard, Money, Loading, EmptyState } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { formatTanggal } from "@/lib/format";

const LEASE_LABEL = {
  active: "Aktif", awaiting_movein: "Menunggu Masuk", scheduled: "Perpanjangan",
  ended: "Selesai", early_exit: "Keluar Lebih Awal", cancelled: "Dibatalkan",
};

const FILTERS = [["all", "Semua"], ["held", "Ditahan"], ["refunded", "Dikembalikan"]];

export default function RiwayatDeposit() {
  const { projectId, projectParam } = useProjects();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["deposits", projectId],
    queryFn: async () => (await api.get("/leases/deposits", { params: projectParam })).data,
  });

  const rows = (data?.rows || []).filter((r) => {
    if (filter === "held" && r.refunded) return false;
    if (filter === "refunded" && !r.refunded) return false;
    if (q && !`${r.resident_name} ${r.room_number} ${r.project_name} ${r.house_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const s = data?.summary || { held: 0, refunded: 0, deducted: 0, count_held: 0, count_refunded: 0 };

  return (
    <div className="space-y-5">
      <PageHeader title="Riwayat Deposit" description="Ringkasan deposit jaminan yang masih ditahan vs sudah dikembalikan ke penghuni." />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard testid="dep-held" label={`Deposit Ditahan (${s.count_held})`} value={<Money value={s.held} />} icon={PiggyBank} accent="#0369a1" hint="Belum dikembalikan" />
        <StatCard testid="dep-refunded" label={`Sudah Dikembalikan (${s.count_refunded})`} value={<Money value={s.refunded} />} icon={Undo2} accent="#047857" hint="Dana keluar via Keuangan Operasional" />
        <StatCard testid="dep-deducted" label="Potongan (Ditahan Pengelola)" value={<Money value={s.deducted} />} icon={Scissors} accent="#b45309" hint="Selisih deposit − pengembalian" />
      </div>

      <SectionCard className="!p-4">
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map(([k, l]) => (
            <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`dep-filter-${k}`}>{l}</Button>
          ))}
          <div className="relative ml-auto w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Cari penghuni / kamar" className="h-9 pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dep-search" />
          </div>
        </div>
      </SectionCard>

      <SectionCard className="!p-0 overflow-hidden">
        {isLoading ? <Loading /> : rows.length === 0 ? (
          <EmptyState title="Belum ada data deposit" description="Deposit muncul saat booking atau Langsung Sewa menyertakan deposit." icon={PiggyBank} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Penghuni</TableHead>
                <TableHead>Kamar</TableHead>
                <TableHead>Status Sewa</TableHead>
                <TableHead className="text-right">Deposit</TableHead>
                <TableHead>Status Deposit</TableHead>
                <TableHead className="text-right">Dikembalikan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.lease_id} data-testid={`dep-row-${r.lease_id}`}>
                  <TableCell className="font-medium">
                    {r.resident_name}
                    <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${r.source === "gh" ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-600"}`} data-testid={`dep-source-${r.lease_id}`}>{r.source === "gh" ? "Guest House" : "Kos"}</span>
                    <div className="text-xs text-muted-foreground">{r.project_name} · {r.house_name}</div>
                  </TableCell>
                  <TableCell>{r.room_number}</TableCell>
                  <TableCell className="text-xs">{LEASE_LABEL[r.lease_status] || r.lease_status}</TableCell>
                  <TableCell className="text-right tabular-nums"><Money value={r.deposit} /></TableCell>
                  <TableCell>
                    {r.refunded
                      ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Dikembalikan</span>
                      : <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">Ditahan</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.refunded ? (
                      <>
                        <Money value={r.refund_amount} />
                        {r.refund_date && <div className="text-xs text-muted-foreground">{formatTanggal(r.refund_date)}</div>}
                        {r.deduction > 0 && <div className="text-[11px] text-amber-700">Potongan <Money value={r.deduction} /></div>}
                      </>
                    ) : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>
    </div>
  );
}
